import { t } from '@/i18n'

import { formatCurrency } from '../fomatter.utils'
import { BODY_FONT, splitLines, wrapText } from '../text.utils'
import type { TicketElement } from '../TicketElement'
import type { Payment } from '../types'

export class PaymentsBox implements TicketElement {
  padding: number
  marginTop: number
  titleMarginBottom: number
  lineHeight: number
  title: string
  payments: Payment[]
  border: boolean
  important: boolean
  titleFont: string
  methodFont: string
  amountFont: string
  font: string

  constructor(
    title: string,
    payments: Payment[],
    {
      border = true,
      padding = 20,
      important = false
    }: { border?: boolean; padding?: number; important?: boolean } = {}
  ) {
    this.padding = padding
    this.marginTop = 8
    this.titleMarginBottom = 10
    this.lineHeight = 22
    this.title = important ? title.toUpperCase() : title
    this.payments = payments
    this.border = border
    this.important = important
    this.titleFont = important ? `bold 18px ${BODY_FONT}` : `18px ${BODY_FONT}`
    this.methodFont = important ? `20px ${BODY_FONT}` : `bold 14px ${BODY_FONT}`
    this.amountFont = important ? `bold 22px ${BODY_FONT}` : `14px ${BODY_FONT}`
    this.font = `14px ${BODY_FONT}`
  }

  height(ctx: CanvasRenderingContext2D, width: number): number {
    ctx.font = this.titleFont
    ctx.textAlign = this.important ? 'center' : 'left'
    const messageWidth = width - this.padding
    const lines = splitLines(ctx, this.title, messageWidth).length
    ctx.font = this.font
    const paymentLines = this.payments.reduce((acc, payment) => {
      return acc + (payment.change || (payment.tip && this.payments.length > 1) ? 1 : 0) + 1
    }, 0)

    return (
      this.marginTop +
      this.titleMarginBottom +
      this.padding * 2 +
      this.lineHeight * (lines + paymentLines)
    )
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): void {
    ctx.textBaseline = 'middle'
    ctx.font = this.titleFont
    ctx.textAlign = this.important ? 'center' : 'left'
    top += this.marginTop
    const messageWidth = width - this.padding * 2
    const height =
      wrapText(
        ctx,
        this.title,
        this.important ? width / 2 : this.padding,
        top + this.padding + this.lineHeight / 2,
        messageWidth
      ) * this.lineHeight
    ctx.font = this.font
    const firstPaymentPosition =
      top + height + this.titleMarginBottom + this.padding + this.lineHeight / 2
    let lineCounter = 0
    for (let i = 0; i < this.payments.length; ++i) {
      ctx.font = this.methodFont
      ctx.textAlign = 'left'
      ctx.fillText(
        this.payments[i].label.charAt(0).toUpperCase() + this.payments[i].label.slice(1) + ':',
        this.padding,
        firstPaymentPosition + lineCounter * this.lineHeight
      )
      ctx.font = this.amountFont
      ctx.textAlign = 'right'
      ctx.fillText(
        formatCurrency(
          this.payments[i].amount - (this.payments[i].tip || 0) + (this.payments[i].change || 0)
        ),
        width - this.padding,
        firstPaymentPosition + lineCounter * this.lineHeight
      )
      if (this.payments[i].change || (this.payments[i].tip && this.payments.length > 1)) {
        lineCounter++
        ctx.font = `14px ${BODY_FONT}`
        ctx.textAlign = 'left'
        ctx.fillText(
          this.payments[i].change ? t('checkout.change') : t('checkout.tip'),
          this.padding + 10,
          firstPaymentPosition + lineCounter * this.lineHeight
        )
        ctx.font = this.amountFont
        ctx.textAlign = 'right'
        ctx.fillText(
          formatCurrency(this.payments[i].change || this.payments[i].tip!),
          width - this.padding,
          firstPaymentPosition + lineCounter * this.lineHeight
        )
      }
      lineCounter++
    }
    const paymentsHeight = this.payments.length * this.lineHeight
    if (this.border) {
      ctx.strokeRect(
        0,
        top,
        width,
        height + paymentsHeight + this.titleMarginBottom + this.padding * 2
      )
    }
  }
}
